import { on } from 'delegated-events'

import { trackEvent } from 'api/index'
import { getCsrfToken } from 'utils/csrf'
import { onDomReady } from 'utils/onDomReady'

import {
  applyFieldState,
  buildFields,
  fieldFor,
  validateField
} from './book-a-demo/fields'

const ROOT = '[data-ebook-form]'
const EVENT_PREFIX = 'Marketing:Ebook'
const ASSET = '4-steps-from-data-chaos'

const SERVER_FIELD_ERRORS = {
  email_public_domain: {
    field: 'email',
    message:
      'Please use your business email. Public email domains are not accepted.'
  }
}

const SERVER_FORM_ERRORS = {
  region_not_supported:
    "We're sorry, this content isn't available in your region at this time."
}

const trackEbookEvent = (action, payload) => {
  trackEvent(`${EVENT_PREFIX}:${action}`, payload)
}

const analyticsPayload = values => ({
  role: values.role,
  industry: values.industry,
  company_size: values.company_size,
  asset: ASSET
})

const submissionErrorReason = ({ code, status }) =>
  code || (status ? `http_${status}` : 'network_error')

const markStarted = root => {
  if (!root || root.dataset.ebookStarted) return
  root.dataset.ebookStarted = 'true'
  trackEbookEvent('Started')
}

const postJson = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken()
    },
    body: JSON.stringify(body)
  })

const revalidateIfPreviouslyInvalid = e => {
  const form = e.currentTarget.form || e.currentTarget.closest('form')
  if (!form) return

  markStarted(form.closest(ROOT))

  const field = fieldFor(buildFields(form), e.target)
  if (field?.control.hasAttribute('aria-invalid')) validateField(field)
}

on(
  'input',
  `${ROOT} input[name]:not([type="checkbox"])`,
  revalidateIfPreviouslyInvalid
)
on(
  'change',
  `${ROOT} [data-demo-multiselect] .multiselect__checkbox`,
  revalidateIfPreviouslyInvalid
)
on('change', `${ROOT} [data-demo-privacy]`, revalidateIfPreviouslyInvalid)

const FORM_VISIBLE_PX = 200

const watchFormInView = root => {
  if (!window.IntersectionObserver) return

  const observer = new IntersectionObserver(
    entries => {
      if (!entries.some(entry => entry.isIntersecting)) return
      observer.disconnect()
      trackEbookEvent('Form viewed')
    },
    { rootMargin: `0px 0px -${FORM_VISIBLE_PX}px 0px` }
  )
  observer.observe(root)
}

onDomReady(() => {
  const root = document.querySelector(ROOT)
  if (!root) return

  root.querySelectorAll('[data-demo-multiselect]').forEach(box => {
    const toggle = box.querySelector('.multiselect__toggle')
    const error = box.querySelector('[data-demo-field-error]')
    if (toggle && error) toggle.setAttribute('aria-describedby', error.id)
  })

  watchFormInView(root)
})

const collectValues = results => {
  const values = {}
  results.forEach(({ field, value }) => {
    if (field.name === 'privacy') {
      values.privacy_agreed = true
    } else {
      values[field.name] = value
    }
  })
  return values
}

const applyServerError = (fields, code) => {
  const handled = SERVER_FIELD_ERRORS[code]
  if (!handled) return false

  const target = fields.find(f => f.name === handled.field)
  if (!target) return false

  applyFieldState(target, handled.message)
  target.control.focus()
  return true
}

const showFormLevelError = (errorMessage, code) => {
  if (!errorMessage.dataset.defaultText) {
    errorMessage.dataset.defaultText = errorMessage.textContent
  }

  errorMessage.textContent =
    SERVER_FORM_ERRORS[code] || errorMessage.dataset.defaultText
  errorMessage.classList.remove('hidden')
}

const openEbookTab = root => {
  const tab = window.open('', '_blank')
  if (tab) {
    tab.opener = null
    if (tab.document?.body) {
      tab.document.title = 'Your e-book'
      tab.document.body.textContent = 'Preparing your copy...'
    }
  }
  return {
    show: () => {
      if (!tab || tab.closed) return false
      tab.location = root.dataset.ebookFile
      return true
    },
    close: () => tab?.close()
  }
}

const rejectUnlessAccepted = async response => {
  if (response.ok) return

  const body = await response.json().catch(() => ({}))
  const error = new Error(`HTTP ${response.status}`)
  error.code = body.error
  error.status = response.status
  throw error
}

const showSuccess = (root, form, email) => {
  const success = root.querySelector('[data-ebook-success]')
  success.querySelector('[data-ebook-success-email]').textContent = email

  form.classList.add('hidden')
  success.classList.remove('hidden')
}

on('submit', `${ROOT} form`, e => {
  e.preventDefault()

  const form = e.currentTarget
  const root = form.closest(ROOT)
  const submitButton = form.querySelector('.demo-form__submit')
  const errorMessage = form.querySelector('[data-demo-form-error]')
  errorMessage.classList.add('hidden')

  markStarted(root)

  const fields = buildFields(form)
  const results = fields.map(field => ({ field, ...validateField(field) }))
  const firstInvalid = results.find(r => r.invalid)

  if (firstInvalid) {
    trackEbookEvent('Validation failed')
    firstInvalid.field.control.focus()
    return
  }

  const values = collectValues(results)
  const payload = analyticsPayload(values)
  const ebookTab = openEbookTab(root)

  submitButton.setAttribute('disabled', 'disabled')

  // Two-argument then: a failure inside the success branch must not be
  // reported as a failed submission the server has already accepted.
  postJson(form.action, values)
    .then(rejectUnlessAccepted)
    .then(
      () => {
        trackEbookEvent('Submitted', { ...payload, status: 'success' })
        showSuccess(root, form, values.email)
        if (ebookTab.show()) {
          trackEbookEvent('Opened', { trigger: 'auto', asset: ASSET })
        }
      },
      error => {
        const { code } = error
        ebookTab.close()
        trackEbookEvent('Submitted', {
          ...payload,
          status: 'error',
          error_reason: submissionErrorReason(error)
        })
        if (code) trackEbookEvent('Rejected', { error_reason: code })

        const shownInline = applyServerError(fields, code)
        if (!shownInline) showFormLevelError(errorMessage, code)
        submitButton.removeAttribute('disabled')
      }
    )
})

on('click', `${ROOT} [data-ebook-open]`, () => {
  trackEbookEvent('Opened', { trigger: 'button', asset: ASSET })
})

on('click', '[data-ebook-cta]', e => {
  const { ebookCta: placement, ebookCtaTarget: target } =
    e.currentTarget.dataset
  trackEbookEvent('Cta clicked', { placement, target })
})
